function scrollToId(id) {
  return (e) => {
    e.preventDefault();
    document.getElementById(id)?.scrollIntoView({ behavior: "smooth", block: "start" });
  };
}

function roleLine(speaker) {
  return [speaker.title, speaker.org].filter(Boolean).join(" · ");
}

/* Lead feature — the headline name, full editorial treatment */
function LeadFeature({ speaker, index }) {
  return (
    <article
      className="speaker-card md:col-span-12 grid grid-cols-1 md:grid-cols-12 gap-7 md:gap-10"
      data-testid={`speaker-card-${index}`}
    >
      <div className="md:col-span-7 lg:col-span-6 feature-plate aspect-[4/5] relative overflow-hidden">
        {speaker.photo_url && (
          <img
            src={speaker.photo_url}
            alt={speaker.name}
            loading="lazy"
            className="absolute inset-0 w-full h-full object-cover img-editorial"
          />
        )}
        <span className="speaker-index">01</span>
      </div>
      <div className="md:col-span-5 lg:col-span-6 flex flex-col justify-end md:pb-2">
        <span className="speaker-tag" data-testid="speaker-lead-tag">
          Opening keynote
        </span>
        <h3
          className="speaker-name text-4xl sm:text-5xl lg:text-[72px] mt-4"
          data-testid={`speaker-name-${index}`}
        >
          {speaker.name}
        </h3>
        <p className="speaker-role mt-5">{roleLine(speaker)}</p>
        {speaker.bio && (
          <p className="text-[15px] md:text-[15.5px] text-white/65 mt-7 max-w-[46ch] leading-[1.65] border-t border-white/15 pt-7">
            {speaker.bio}
          </p>
        )}
      </div>
    </article>
  );
}

/* Grid card — compact, disciplined */
function SpeakerCard({ speaker, index }) {
  const num = String(index + 1).padStart(2, "0");
  return (
    <article className="speaker-card flex flex-col" data-testid={`speaker-card-${index}`}>
      <div className="feature-plate aspect-[4/5] relative overflow-hidden">
        {speaker.photo_url && (
          <img
            src={speaker.photo_url}
            alt={speaker.name}
            loading="lazy"
            className="absolute inset-0 w-full h-full object-cover img-editorial"
          />
        )}
        <span className="speaker-index">{num}</span>
      </div>
      <h3
        className="speaker-name text-[22px] md:text-2xl mt-5"
        data-testid={`speaker-name-${index}`}
      >
        {speaker.name}
      </h3>
      <p className="speaker-role mt-2">{roleLine(speaker)}</p>
    </article>
  );
}

export default function Speakers({ speakers, loaded }) {
  const confirmed = (speakers || []).filter((s) => s.confirmed).slice(0, 7);
  const hasAny = confirmed.length > 0;
  const [lead, ...rest] = confirmed;

  return (
    <section
      id="speakers"
      data-testid="speakers-section"
      className="section section-dark bg-[var(--dark)] text-[var(--bg)]"
    >
      <div className="max-w-page container-x">
        {/* Header */}
        <div className="grid grid-cols-1 md:grid-cols-12 gap-6 md:gap-10 mb-12 md:mb-16">
          <div className="md:col-span-7">
            <div className="flex items-center gap-3 mb-6">
              <span className="section-index">01</span>
              <span className="eyebrow eyebrow--accent" data-testid="speakers-eyebrow">
                Featured speakers
              </span>
            </div>
            <h2 className="font-display text-4xl sm:text-5xl md:text-6xl leading-[0.98] tracking-[-0.02em] max-w-[18ch]">
              On stage.
            </h2>
          </div>
          <div className="md:col-span-5 flex md:items-end md:justify-end">
            <p
              className="text-[14px] md:text-[15px] leading-[1.55] text-white/60 max-w-[38ch]"
              data-testid="speakers-note"
            >
              {hasAny
                ? "Frontier researchers, senior operators and decision-makers — Nordic and international. More names announced through the year."
                : "First names announced soon. Early Access hears first."}
            </p>
          </div>
        </div>

        {!hasAny && (
          <div
            className="border-t border-white/15 pt-8 flex flex-col md:flex-row md:items-end md:justify-between gap-6"
            data-testid="speakers-tease"
          >
            <p className="text-[16px] md:text-[17px] text-white/85 max-w-[52ch] leading-[1.5]">
              The line-up is being curated privately. When a speaker is signed,
              they appear here as a full feature — not a placeholder card.
            </p>
            <a
              href="#early-access"
              onClick={scrollToId("early-access")}
              className="text-link shrink-0"
              data-testid="speakers-tease-cta"
            >
              Join Early Access <span aria-hidden>→</span>
            </a>
          </div>
        )}

        {hasAny && (
          <div className="border-t border-white/15 pt-12 md:pt-16 flex flex-col gap-14 md:gap-20">
            {lead && <LeadFeature speaker={lead} index={0} />}
            {rest.length > 0 && (
              <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-x-8 gap-y-12">
                {rest.map((s, i) => (
                  <SpeakerCard key={s.id} speaker={s} index={i + 1} />
                ))}
              </div>
            )}
            <div className="border-t border-white/15 pt-6 flex flex-col md:flex-row md:items-center md:justify-between gap-4">
              <p className="text-[12px] tracking-[0.16em] uppercase text-white/40" data-testid="speakers-more">
                12+ speakers · announced through the year
              </p>
              <a
                href="#early-access"
                onClick={scrollToId("early-access")}
                className="sponsor-link w-fit"
                data-testid="speakers-announce-cta"
              >
                Get announcements first
                <span aria-hidden>→</span>
              </a>
            </div>
          </div>
        )}

        {!loaded && (
          <p className="text-[12px] text-white/40 mt-8" data-testid="speakers-loading">
            Loading…
          </p>
        )}
      </div>
    </section>
  );
}
